import { useEffect, useState } from "react";
import { useAppDispatch } from "../../app/hook";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  SpaceBetween,
  ColumnLayout,
  Form,
  Box,
} from "@cloudscape-design/components-themed";
import {
  usePostGatewaysRegisterMutation,
  usePutGatewaysUpdateMutation,
} from "../../features/api";
import { showModal } from "../UI/modal/modal-slice";
import { COMMON_LABELS } from "../default-constants";
import {
  CorporateNumberInfo,
  GatewayRegister,
  GatewayUpdate,
  RegisterAddressInfo,
} from "../../features/api-interfaces";

import { Tab } from "./Tab";
import { AddressFormUI } from "../UI/address/MainForm";
import { checkAddress, registerAddressData } from "../UI/address/AddressForm";
import {
  checkCorporate,
  registerCorporateData,
} from "../UI/address/CorporateNumberSearch";
import { USER_TYPE } from "../../configs/constants";
import { useCanIShowHook } from "../../app/useCanIShowHook";

export const GwCompanyForm = (props: any) => {
  // 事業者種別取得
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [postGateways] = usePostGatewaysRegisterMutation();
  const [putGateways] = usePutGatewaysUpdateMutation();
  const { code } = useParams();

  // 登録画面か更新画面かのフラグ
  const isRegister = code ? false : true;

  // データをセット
  let gatewayCompanyData: GatewayRegister = props.gatewayCompanyData;

  // GW事業者情報
  const [gatewayRegister, setGatewayRegister] = useState({
    corporateNumber: "",
    gwCompanyName: "",
    gwCompanyNameKana: "",
    address: {} as RegisterAddressInfo,
  } as GatewayRegister);

  // 所在情報
  const [address, setAddress] = useState({
    postalCode: "",
    address1: "",
    address1Kn: "",
    address2: "",
    address2Kn: "",
    prfCd: "",
    ctyCd: "",
    lrgCd: "",
    chrCd: "",
    phoneNumber: "",
  } as RegisterAddressInfo);

  //法人番号情報
  const [corporateData, setCorporateData] = useState({
    corporateNumber: "",
    companyName: "",
    companyNameKana: "",
  } as CorporateNumberInfo);

  useEffect(() => {
    // 更新の場合、DBに登録済みのデータを取得する
    if (gatewayCompanyData !== undefined) {
      setGatewayRegister(gatewayCompanyData);
      setAddress(gatewayCompanyData.address);
      setCorporateData({
        corporateNumber: gatewayCompanyData.corporateNumber,
        companyName: gatewayCompanyData.gwCompanyName,
        companyNameKana: gatewayCompanyData.gwCompanyNameKana,
      });
    }
  }, [gatewayCompanyData]);

  //戻るボタンの表示確認フラグ（入力フォームに何か入力された場合はtrue）
  const [isFormEntered, setIsFormEntered] = useState(false);

  // 登録処理
  const gatewayRegist = () => {
    //バリエーションチェック
    const isCorporateValid = checkCorporate();
    const isAddressValid = checkAddress();

    if (!isCorporateValid || !isAddressValid) return;

    //入力した内容を詰める
    gatewayRegister.corporateNumber = registerCorporateData.corporateNumber;
    gatewayRegister.gwCompanyName = registerCorporateData.companyName;
    gatewayRegister.gwCompanyNameKana = registerCorporateData.companyNameKana;
    gatewayRegister.address = registerAddressData;
    // 登録処理API呼び出し
    postGateways(gatewayRegister).then((result: any) => {
      if (!("error" in result) && "error" !== result.data.status) {
        navigate("/gateway-companies");
      }
    });
  };

  // 登録ボタン押下イベント
  const onClickRegisterFormButton = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.REGISTER_CONFIRM,
        positiveFn: () => {
          gatewayRegist();
        },
      })
    );
  };

  // 更新処理
  const gatewayUpdate = () => {
    //バリエーションチェック
    const isCorporateValid = checkCorporate();
    const isAddressValid = checkAddress();

    if (!isCorporateValid || !isAddressValid) return;

    //更新処理呼び出し
    const gatewayUpdate = {
      corporateNumber: registerCorporateData.corporateNumber,
      gwCompanyName: registerCorporateData.companyName,
      gwCompanyNameKana: registerCorporateData.companyNameKana,
      address: registerAddressData,
    } as GatewayRegister;
    const gatewayUpdateData = {
      gwCompanyCode: code,
      data: gatewayUpdate,
    } as GatewayUpdate;
    putGateways(gatewayUpdateData).then((result: any) => {
      if (!("error" in result) && "error" !== result.data.status) {
        navigate("/gateway-companies");
      }
    });
  };

  // 更新ボタン押下イベント
  const onClickUpdateFormButton = () => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.UPDATE_CONFIRM,
        positiveFn: () => {
          gatewayUpdate();
        },
      })
    );
  };

  //戻るボタン押下時のダイアログ
  const returnToList = () => {
    if (!isFormEntered) {
      navigate("/gateway-companies");
    } else {
      dispatch(
        showModal({
          title: COMMON_LABELS.TITLES.DL,
          content: COMMON_LABELS.CONTENTS.PAGE_MOVE_CONFIRM,
          positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
          negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
          positiveFn: () => {
            navigate("/gateway-companies");
          },
        })
      );
    }
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      <form onSubmit={(e) => e.preventDefault()}>
        <SpaceBetween direction="vertical" size="l">
          <Form>
            <AddressFormUI
              addressData={address}
              setAddressInfoData={setAddress}
              corporateData={corporateData}
              setCorporateData={setCorporateData}
              setIsFormEntered={setIsFormEntered}
              isRegister={isRegister}
              showCompanyCode={props.gwCompanyCode}
              isUpdatable={isDocomoUser}
            />
          </Form>
          <ColumnLayout columns={2}>
            {isRegister && (
              <Box float="left">
                <Button formAction="none" variant="link" onClick={returnToList}>
                  {COMMON_LABELS.BTNS.BACK}
                </Button>
              </Box>
            )}
            {isRegister && (
              <Box float="right">
                <Button onClick={onClickRegisterFormButton} variant="primary">
                  {COMMON_LABELS.BTNS.REGISTER}
                </Button>
              </Box>
            )}
          </ColumnLayout>
          {!isRegister && (
            <Box float="right">
              <Button onClick={onClickUpdateFormButton} variant="primary">
                {COMMON_LABELS.BTNS.UPDATE}
              </Button>
            </Box>
          )}
        </SpaceBetween>
      </form>

      {!isRegister && (
        <Tab
          gatewayCompanyContractData={props.gatewayCompanyContractData}
          isFetching={props.isFetching}
        />
      )}

      {!isRegister && (
        <Box textAlign="left">
          <Button formAction="none" variant="link" onClick={returnToList}>
            {COMMON_LABELS.BTNS.BACK}
          </Button>
        </Box>
      )}
    </SpaceBetween>
  );
};
